import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { toast } from "react-hot-toast";
import { useCustomEventListener } from "react-custom-events";
import PlacesAutocomplete from "../form/PlacesAutocomplete";
import GoogleMaps from "../atoms/GoogleMaps";
import MultiSelect from "../MultiSelect";

const LocationModal = ({ isOpen, onClose, initialData = null, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [manualAddress, setManualAddress] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [isAddressDescription, setIsAddressDescription] = useState(false);

  const categoriesList = [
    { label: "Food & Beverage", value: "Food & Beverage" },
    { label: "Hospitality", value: "Hospitality" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Fitness", value: "Fitness" },
    { label: "Apartments", value: "Apartments" },
    { label: "Logistics", value: "Logistics" },
    { label: "Retail", value: "Retail" },
    { label: "Other", value: "Other" },
  ];

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    contact_number: "",
    categories: [],
    description: "",
  });

  useEffect(() => {
    // Reset form when modal opens
    if (isOpen) {
      if (initialData) {
        // Set form data for edit mode
        const newFormData = {
          name: initialData.name || "",
          address: initialData.address || "",
          contact_number: initialData.contact_number || "",
          categories: initialData.categories || [],
          description: initialData.description || "",
        };
        setFormData(newFormData);

        // Set address data for display
        const newAddressData = {
          address: initialData.address,
          lat: initialData.lat,
          lng: initialData.lng,
        };
        setAddressData(newAddressData);

        // Enable address display
        setIsAddressDescription(true);
        setManualAddress(true); // Enable manual mode for edit
      } else {
        // Reset all states for new location
        setFormData({
          name: "",
          address: "",
          contact_number: "",
          categories: [],
          description: "",
        });
        setAddressData(null);
        setIsAddressDescription(false);
        setManualAddress(false);
      }
    }
  }, [initialData, isOpen]); // Added isOpen to dependency array

  const validateForm = () => {
    const newErrors = {};

    if (!formData.address || !formData.address.trim()) {
      newErrors.address = "Business address is required";
    }

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Business name is required";
    }

    if (!formData.contact_number || !formData.contact_number.trim()) {
      newErrors.contact_number = "Contact number is required";
    }

    if (!formData.categories || formData.categories.length === 0) {
      newErrors.categories = "Please select at least one category";
    }

    if (!formData.description || !formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInput = (place) => {
    if (place) {
      setFormData((prev) => ({
        ...prev,
        address: place.formatted_address || place.name,
        name: !manualAddress ? place.name : "",
        lat: place.geometry?.location?.lat() || null,
        lng: place.geometry?.location?.lng() || null,
      }));

      setAddressData({
        address: place.formatted_address || place.name,
        lat: place.geometry?.location?.lat() || null,
        lng: place.geometry?.location?.lng() || null,
      });

      setIsAddressDescription(!!place.formatted_address);
    }
  };

  useCustomEventListener("placeSelected", (data) => {
    const Name = data.business_name || data.terms[0]?.value;
    const cityD = data.terms[data.terms.length - 3]?.value;
    const State = data.terms[data.terms.length - 2]?.value;
    const ZipCode = data.zipCode ? data.zipCode : "00000";
    const address = {
      name: Name,
      address: data.description,
      city: cityD,
      state: State,
      zip: ZipCode,
      lat: data.lat,
      lng: data.lng,
      website: data.website,
      categories: data.categories,
      business_number: data.business_number,
    };
    setAddressData(address);
    setIsAddressDescription(true);
    setFieldValue("address", data.description);
    if (data.business_name) {
      setFieldValue("name", data.business_name);
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getErrorMessage = (field) => {
    return errors[field] || "";
  };

  const setMapSelectAddress = (address) => {
    setAddressData(address);
    setFieldValue("address", address?.address);
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      name: "",
      address: "",
      contact_number: "",
      categories: [],
      description: "",
    });
    // Reset other states
    setAddressData(null);
    setIsAddressDescription(false);
    setManualAddress(false);
    setErrors({});
    // Close modal
    onClose();
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        onSave(formData);
        toast.success(
          initialData
            ? "Location updated successfully"
            : "Location added successfully"
        );
        onClose();
      } catch (error) {
        toast.error("Something went wrong. Please try again.");
      }
    } else {
      toast.error("Please fill all required fields correctly");
    }
  };

  return (
    <Modal
      title={null}
      visible={isOpen}
      onCancel={handleClose}
      closable={true}
      footer={null}
      width={600}
      className="max-h-[90vh] overflow-auto"
    >
      <div className="w-full">
        {/* Modal Header */}
        <div className="relative w-full bg-primary-500 px-4 py-3 sticky top-0 z-10">
          <h2 className="text-lg text-white font-medium">
            {initialData ? "Edit Location" : "Add Location"}
          </h2>
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-white/80 hover:text-white transition-colors"
            onClick={handleClose}
          >
            <i className="far fa-times text-lg" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4">
          <div className="space-y-4">
            <div>
              <div>
                {manualAddress ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.address
                          ? "border-red-500"
                          : "border-gray-300 focus:border-primary-500"
                      } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                      placeholder="Enter business address"
                    />
                  </div>
                ) : (
                  <PlacesAutocomplete
                    label="Location Address"
                    pValue={formData.address}
                    isAddressSearch={false}
                    setAddressCheck={handleInput}
                    className="text-base"
                  />
                )}
                {getErrorMessage("address") && (
                  <p
                    className={`mt-1 text-xs text-red-500 ${
                      !manualAddress && !addressData?.address && "mb-2"
                    }`}
                  >
                    {getErrorMessage("address")}
                  </p>
                )}
              </div>

              {!manualAddress &&
                !addressData?.address &&
                !isAddressDescription && (
                  <button
                    onClick={() => {
                      setFieldValue("name", "");
                      setManualAddress(true);
                    }}
                    className="text-xs text-gray-500 mt-2 text-base"
                  >
                    Not on Google?{" "}
                    <span className="text-gray-800 underline cursor-pointer text-xs md:text-sm">
                      Enter Business Address manually
                    </span>
                  </button>
                )}
            </div>

            {manualAddress && (
              <div className="mt-3">
                <GoogleMaps
                  location={{
                    address: addressData?.address,
                    lat: addressData?.lat,
                    lng: addressData?.lng,
                  }}
                  onClickAction={setMapSelectAddress}
                  height="200px"
                />
              </div>
            )}

            {((addressData?.address && isAddressDescription) ||
              manualAddress) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.name
                      ? "border-red-500"
                      : "border-gray-300 focus:border-primary-500"
                  } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                  placeholder="Enter business name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.name}
                  </p>
                )}
              </div>
            )}

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories
              </label>
              <div className="border border-warmgray-300 rounded-md">
                <MultiSelect
                  name="categories"
                  value={
                    formData.categories?.map((cat) => ({
                      value: cat,
                      label: cat,
                    })) || []
                  }
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      "categories",
                      selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : []
                    );
                  }}
                  options={categoriesList}
                  isMulti={true}
                  placeholder="Select categories"
                  className={errors.categories ? "is-invalid" : ""}
                />
              </div>
              {errors.categories && (
                <p className="mt-1 text-sm text-red-500">{errors.categories}</p>
              )}
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number
              </label>
              <input
                type="tel"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.contact_number
                    ? "border-red-500"
                    : "border-gray-300 focus:border-primary-500"
                } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Enter contact number"
              />
              {errors.contact_number && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.contact_number}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.description
                    ? "border-red-500"
                    : "border-gray-300 focus:border-primary-500"
                } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Enter business description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.description}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <i className="fad fa-circle-notch fa-spin mr-2" />
              ) : null}
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LocationModal;
